// What the scheduler is told after a job shift (#456).
//
// The whole point of this module is one sentence: ⇄ Shift moves CREW ONLY, and
// the job's phase dates stay where they are. That silence is what #452/#453
// actually were — the shift moved a week of crew, said nothing about the
// schedule dates, and the dates then got re-typed by hand with a day lost in
// the process. The shift could not have moved them (`planned_start` is only
// writable through PATCH /projects/:id/phases, which the manpower module never
// calls), so the honest fix is to say so.
//
// ⚠️ An automated date move WAS built — #461, `mp_schedule_draft_date_moves`,
// staged intent claimed at publish — and was dropped after nine review rounds
// turned up a continuing stream of concurrency defects, most of them in the
// fixes for earlier ones (product owner, 2026-09-09). Do not reach for it again without
// reading that PR: the hard part was never the arithmetic, it was coupling a
// staged date move to the crew drafts it belonged to.
//
// In lib/ so the wording can be tested directly, and because the wording IS the
// feature here.

/** How many days, with a direction: `+7 days`, `−3 days`, `+1 day`. */
export function signedDays(offsetDays: number): string {
  const n = Math.abs(offsetDays);
  return `${offsetDays > 0 ? '+' : '−'}${n} day${n === 1 ? '' : 's'}`;
}

/**
 * The full notice for a staged shift.
 *
 * Says three things, in the order they matter: what moved, that it is staged
 * rather than live, and that the phase dates did NOT move — with where they
 * live, so the scheduler knows what to go and look at.
 */
export function shiftNotice(opts: {
  jobNumber: string;
  offsetDays: number;
  /** Crew days actually moved. */
  movedDays: number;
  /**
   * Whether this row has a project page to point at.
   *
   * ⚠️ FALSE for a maintenance sub-project, and the dates sentence is then
   * omitted entirely. Shift is offered on those rows — the button excludes only
   * the maintenance PARENT — but `getProjects` filters
   * `parent_project_id IS NULL`, so a schedule-only child has no workspace and
   * no Schedule & Dates card. Naming the reason rather than the shape, because
   * what matters is that there is somewhere to send them.
   */
  hasProjectPage: boolean;
}): string {
  const { jobNumber, offsetDays, movedDays, hasProjectPage } = opts;
  const days = `${movedDays} crew day${movedDays === 1 ? '' : 's'}`;

  // ⚠️ "NOT moved" is stated even though nothing ever moved them, because the
  // reasonable assumption on seeing a job slide a week is that its dates slid
  // with it. Reporting only what changed is what made this look like a bug.
  //
  // ⚠️ IT POINTS AT A PLACE, IT DOES NOT NAME A PERSON — one sentence for
  // everyone, deliberately. An earlier version branched on `projects.edit` to
  // say either "update them" or "ask the PM", and BOTH can be wrong: the phases
  // route also carries @BlockIfEditLocked, so on an unawarded SYSTAC bid
  // nobody can change those dates — not the scheduler, not the PM — while the
  // #411 carve-out deliberately still lets the crew shift through. Being sure
  // enough to name who should act needs the permission AND the job's lifecycle,
  // and the schedule feed carries only an `is_systac` FLAG — which is exactly
  // what must not be keyed off, because the lock is `bid_closed_won_at` plus
  // the prefix and a won bid keeps its SYSTAC- number until it is renumbered.
  //
  // So it names the destination. The workspace already explains its own locks
  // when you get there, from data it actually has. Guessing a second condition
  // in a toast is what made the first version wrong.
  //
  // ⚠️ And omitted entirely for a row with no project page. A schedule-only
  // sub-project holds crew and nothing else — it has no phase dates of its own,
  // so there is nothing to warn about, and pointing at the PARENT's card would
  // be worse than silence: the parent's dates describe the parent, and a child's
  // crew moving is not a reason to change them.
  const dates = hasProjectPage
    ? `⚠️ Phase dates NOT moved — if the job really slipped, its dates are on `
      + `the project's Schedule & Dates card. `
    : '';

  return `Shifted ${jobNumber} manpower by ${signedDays(offsetDays)} — ${days} moved `
    + `(staged — publish to make it live). ${dates}`
    + `Review Safety and Survey visits for conflicts.`;
}
